import type { AppDispatch } from '@redux/store'
// @ts-expect-error TS7016
import cloneDeep from 'lodash/cloneDeep'

import { deepSetObject } from '@domain-group/javascript-utils'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as userActions from '@redux/modules/user'

import defaultFormState, { requiredFields } from '@models/user'

import type { DeleteParams } from '@redux/modules/types'
import type { ImageModel, RoleModel } from '@domain-group/realhub-backend-api-types'
import type { UserModel, UserRequestOptions } from '@domain-group/realhub-backend-api-types'

type CreateUserParams = {
  dispatch: AppDispatch,
  userParams: Partial<UserModel>,
  requestOptions?: UserRequestOptions,
}

const createUser = (params: CreateUserParams) => {
  const { userParams, dispatch, requestOptions } = params
  const { createUser: createFn } = userActions

  return dispatch(createFn(userParams, requestOptions))
}

type UpdateUserParams = {
  dispatch: AppDispatch,
  user: UserModel,
  userParams: Partial<UserModel>,
  requestOptions?: UserRequestOptions,
}

const updateUser = (params: UpdateUserParams) => {
  const {
    dispatch, user, userParams, requestOptions,
  } = params

  const { options } = userParams

  const { updateUser: updateFn } = userActions

  const updatedParams = {
    id: user.id,
    ...userParams,
  }

  // Prevent overwriting user options accidentally
  // in case all options are not passed back in params object
  if (options){
    const userOptions = user.options || {}
    // @ts-expect-error TS2339
    updatedParams.options = JSON.stringify({
      ...cloneDeep(userOptions),
      ...options,
    })
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type SaveUserDashboardParams = {
  activeLayout: object,
  dashboardKey: string,
  dispatch: AppDispatch,
  requestOptions?: object,
  user: UserModel,
}

const saveUserDashboard = (params: SaveUserDashboardParams) => {
  const {
    activeLayout, dashboardKey, dispatch, requestOptions, user,
  } = params

  const updatedUser = deepSetObject(user, `options.custom_dashboards.${dashboardKey}`, activeLayout)

  return updateUser({
    // @ts-expect-error TS2322
    user, userParams: updatedUser, dispatch, requestOptions,
  })
}

type DeleteUserParams = {
  dispatch: AppDispatch,
  user: DeleteParams<UserModel>,
}

const deleteUser = (params: DeleteUserParams) => {
  const { user, dispatch } = params
  const { deleteUser: deleteFn } = userActions

  return dispatch(deleteFn(user))
}

type LoginToRealhub = {
  dispatch: AppDispatch,
  entityParams: {
    redirect: string,
  },
  user: DeleteParams<UserModel>,
}

const loginToRealhub = (params: LoginToRealhub) => {
  const { entityParams: { redirect: redirectUrl }, user, dispatch } = params

  const { loginToRealhub: loginFn } = userActions

  const updatedOptions = {
    origin_redirect_url: window.location.href,
    origin_app_name: 'Template Manager',
    redirect: redirectUrl,
  }

  return dispatch(loginFn(user.id, updatedOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      return {
        success,
        redirectUrl: data.url,
      }
    }

    return response
  })
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useUserForm(
  user: Partial<UserModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const userForm = useForm(
    defaultFormState,
    { entity: user, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [user.id, user.cache_key],
  )

  return {
    ...userForm,
  }
}

// Return related entities
export const useRelations = (entity: Partial<UserModel> = {}) => {
  const { image: default_image_id, role_id } = entity

  const { images, roles } = useSelector(reduxState => reduxState.entities)

  // @ts-expect-error TS2538
  const image: ImageModel = default_image_id && images[default_image_id] ? images[default_image_id] : {}
  const role: Partial<RoleModel> = role_id && roles[role_id] ? roles[role_id] : {}

  return {
    image,
    role,
  }
}

function useUser(initEntity: Partial<UserModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: user }: { entity: UserModel } = useLatestEntity(initEntity, 'users')

  const dispatch = useDispatch()

  const { loading } = useSelector(reduxState => reduxState.users)

  return {
    callbacks: {
      createUser: (userParams: Partial<UserModel>, entityRequestOptions?: UserRequestOptions) => (
        createUser({ userParams, dispatch, requestOptions: entityRequestOptions })
      ),
      deleteUser: (entityRequestOptions?: UserRequestOptions) => (
        // @ts-expect-error TS2353
        deleteUser({ dispatch, user, requestOptions: entityRequestOptions })
      ),
      loginToRealhub: (entityParams: { redirect: string }) => loginToRealhub({ entityParams, user, dispatch }),
      saveUserDashboard: (
        dashboardKey: string,
        activeLayout: object,
        entityRequestOptions?: object,
      ) => saveUserDashboard({
        activeLayout, dashboardKey, dispatch, user, requestOptions: entityRequestOptions,
      }),
      updateUser: (userParams: Partial<UserModel>, entityRequestOptions?: UserRequestOptions) => (
        updateUser({
          user, userParams, dispatch, requestOptions: entityRequestOptions,
        })
      ),
    },
    user,
    loading,
  }
}

export default useUser
